// ════════════════════════════════════════════════════════════
//  frontRow.js — THE FRONT ROW: sit in a real seat and watch the fight
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-17: "what if you sit down and watch the fight from a real seat
//  (you don't have to keep that camera view, you can switch to the battle one that
//  tracks the fighters) … you can actually watch the fight from a 'front row' view
//  where you're in the stand and can look around to see the other fans."
//
//  A SEAT = { i, row, k }: island i (index into ISLANDS), tread `row`, fan number `k`
//  along that tread. It is the very same arithmetic seatPlan() uses to deal the crowd,
//  so the camera sits exactly where a fan sits (and that fan is hidden — the Seats
//  shader's uHide). The seat rides its island's bob (islandBob), so you float with it.
//
//  This file is pure maths + the saved choice; BattleCamera.jsx does the looking and
//  ui/FrontRow.jsx the buttons.
// ════════════════════════════════════════════════════════════
import { ISLANDS, islandBob } from "./StadiumCrowd.jsx";
import { SEAT_GAP, STADIUM, DECK_ORDER } from "../data/ringside.js";

// ── the dials ────────────────────────────────────────────────
export const FRONT = {
  eye: 1.5,          // ⟵ DIAL eye height above the tread (a fan is ~1.7–2.4 tall, seated)
  fovFree: 58,       // ⟵ DIAL field of view while you look around by hand (about what eyes see)
  fovMin: 12,        // ⟵ DIAL tightest the "tracks the fighters" view will go (binoculars)
  fovMax: 40,        // ⟵ DIAL widest the tracking view will go
  pad: 8,            // ⟵ DIAL world units of room kept round the two fighters when framing them
  trackRate: 5,      // ⟵ DIAL how fast the head turns to follow the fight
  lookRate: 16,      // ⟵ DIAL how fast it follows your hand when you look around
  zoom: [0.35, 2.2], // ⟵ DIAL wheel zoom range (multiplies the field of view)
  sens: 0.0045,      // ⟵ DIAL radians of turn per pixel of drag (at the free field of view)
};
export const DEFAULT_SEAT_ROW = 0;   // ⟵ DIAL the first seat you get: on the Close Deck, this tread (0 = nearest the plate)

// ── the arithmetic seatPlan() uses ───────────────────────────
const rowRadius = (isle, row) => isle.deck.r0 + (row + 0.5) * isle.deck.dr;
export const seatsIn = (i, row) => Math.max(1, Math.floor((ISLANDS[i].len * rowRadius(ISLANDS[i], row)) / SEAT_GAP));
const TAU = Math.PI * 2;
const circ = (a, b) => { let d = (a - b) % TAU; if (d > Math.PI) d -= TAU; if (d < -Math.PI) d += TAU; return Math.abs(d); };
export const clampSeat = (s) => {
  const isle = ISLANDS[s.i], row = Math.max(0, Math.min(isle.deck.rows - 1, s.row | 0));
  return { i: s.i, row, k: Math.max(0, Math.min(seatsIn(s.i, row) - 1, s.k | 0)) };
};
const seatAngle = (s) => ISLANDS[s.i].phi0 + ((s.k + 0.5) / seatsIn(s.i, s.row)) * ISLANDS[s.i].len;

// where the seat is right now (t = clock seconds; the island floats)
export function seatWorld(seat, t) {
  const s = clampSeat(seat), isle = ISLANDS[s.i], d = isle.deck;
  const a = seatAngle(s), r = rowRadius(isle, s.row);
  return { x: Math.sin(a) * r, z: Math.cos(a) * r, y: d.y0 + s.row * d.dy + islandBob(s.i, t), a };
}

export function defaultSeat() {
  const di = STADIUM.findIndex((d) => d.name === "close"), i = ISLANDS.findIndex((x) => x.di === (di >= 0 ? di : DECK_ORDER[0]) && x.s === 1);
  return clampSeat({ i, row: DEFAULT_SEAT_ROW, k: Math.floor(seatsIn(i, DEFAULT_SEAT_ROW) / 2) });
}

const nearestIsland = (di, a) => {
  let best = -1, bd = 9;
  ISLANDS.forEach((x, n) => { if (x.di !== di) return; const d = circ(x.phi0 + x.len / 2, a); if (d < bd) { bd = d; best = n; } });
  return best;
};
const kAt = (i, row, a) => {
  const isle = ISLANDS[i], n = seatsIn(i, row);
  const d = ((((a - (isle.phi0 + isle.len / 2)) % TAU) + TAU + Math.PI) % TAU) - Math.PI;   // signed turn from the island's middle
  const frac = Math.max(0, Math.min(1, 0.5 + d / isle.len));
  return Math.max(0, Math.min(n - 1, Math.round(frac * n - 0.5)));
};

// ← → : one fan along; at the end of an island, hop to the next one in the same deck
export function stepSeat(seat, dk) {
  const s = clampSeat(seat), isle = ISLANDS[s.i], n = seatsIn(s.i, s.row);
  let k = s.k + dk, i = s.i;
  if (k >= n || k < 0) {
    const secs = STADIUM[isle.di].sections;
    i = ISLANDS.findIndex((x) => x.di === isle.di && x.s === (isle.s + (dk > 0 ? 1 : -1) + secs) % secs);
    k = dk > 0 ? 0 : seatsIn(i, s.row) - 1;
  }
  return clampSeat({ i, row: s.row, k });
}
// ↑ ↓ : one tread back/forward; past the last tread you climb to the top deck (and back down)
export function stepRow(seat, dr) {
  const s = clampSeat(seat), isle = ISLANDS[s.i], a = seatAngle(s);
  let row = s.row + dr, i = s.i;
  const pos = DECK_ORDER.indexOf(isle.di);
  if (row >= isle.deck.rows) {
    if (pos + 1 < DECK_ORDER.length) { i = nearestIsland(DECK_ORDER[pos + 1], a); row = 0; } else row = isle.deck.rows - 1;
  } else if (row < 0) {
    if (pos - 1 >= 0) { i = nearestIsland(DECK_ORDER[pos - 1], a); row = ISLANDS[i].deck.rows - 1; } else row = 0;
  }
  return clampSeat({ i, row, k: kAt(i, row, a) });
}
// words for the seat label: deck key + 1-based row + 1-based seat
export const seatLabelParts = (seat) => {
  const s = clampSeat(seat), isle = ISLANDS[s.i];
  return { deck: STADIUM[isle.di].name, row: s.row + 1, seat: s.k + 1 };
};

// ── the saved choice (a per-viewer convenience: wrapped, never load-bearing) ──
const KEY = "shatterproof.frontrow.v3";   // v2: the Close Deck exists, old saved seats start over
const sane = (o) => {
  try {
    if (!o || !Number.isInteger(o.i) || !ISLANDS[o.i] || !Number.isInteger(o.row) || !Number.isInteger(o.k)) return defaultSeat();
    return clampSeat(o);
  } catch (e) { return defaultSeat(); }
};
export function loadPrefs() {
  try {
    const o = JSON.parse(localStorage.getItem(KEY) || "null");
    if (o && (o.view === "front" || o.view === "broadcast" || o.view === "sky")) return { view: o.view, seat: sane(o.seat) };
  } catch (e) { /* private window / blocked storage — fall through */ }
  return { view: "broadcast", seat: defaultSeat() };
}
export function savePrefs(view, seat) {
  try { localStorage.setItem(KEY, JSON.stringify({ view, seat })); } catch (e) { /* ignore */ }
}
